import axios from "axios";
import { AuthenticationHelper } from "../utils/AuthenticationHelper";
import { ApplicationApiEndpoint } from "../utils/Constants";

export class StrongFoundationService {
    private readonly authHelper = new AuthenticationHelper();
    private getToken = () => this.authHelper.fetchTokenAsync();

    private is401 = (ex: unknown): boolean => {
        return axios.isAxiosError(ex) && ex.response?.status === 401;
    };

    private refreshToken = async (): Promise<string> => {
        return this.authHelper.fetchTokenAsync(true);
    };

    protected get = async <T>(endpoint: string, params: unknown = undefined): Promise<T | undefined> => {
        try {
            const response = await axios.get<T>(`${ApplicationApiEndpoint}${endpoint}`, {
                headers: { authorization: `Bearer ${await this.getToken()}` },
                params,
            });
            return response.data;
        } catch (ex) {
            if (this.is401(ex)) {
                try {
                    const response = await axios.get<T>(`${ApplicationApiEndpoint}${endpoint}`, {
                        headers: { authorization: `Bearer ${await this.refreshToken()}` },
                        params,
                    });
                    return response.data;
                } catch {
                    return undefined;
                }
            }
            return undefined;
        }
    };

    protected delete = async (endpoint: string, params: unknown = undefined): Promise<boolean> => {
        try {
            await axios.delete(`${ApplicationApiEndpoint}${endpoint}`, {
                headers: { authorization: `Bearer ${await this.getToken()}` },
                params,
            });
            return true;
        } catch (ex) {
            if (this.is401(ex)) {
                try {
                    await axios.delete(`${ApplicationApiEndpoint}${endpoint}`, {
                        headers: { authorization: `Bearer ${await this.refreshToken()}` },
                        params,
                    });
                    return true;
                } catch {
                    return false;
                }
            }
            return false;
        }
    };

    protected post = async <T>(endpoint: string, body: unknown = undefined): Promise<T | undefined> => {
        try {
            const response = await axios.post<T>(`${ApplicationApiEndpoint}${endpoint}`, body, {
                headers: { authorization: `Bearer ${await this.getToken()}` },
            });
            return response.data;
        } catch (ex) {
            if (this.is401(ex)) {
                try {
                    const response = await axios.post<T>(`${ApplicationApiEndpoint}${endpoint}`, body, {
                        headers: { authorization: `Bearer ${await this.refreshToken()}` },
                    });
                    return response.data;
                } catch {
                    return undefined;
                }
            }
            return undefined;
        }
    };

    protected put = async <T>(endpoint: string, body: unknown = undefined): Promise<T | undefined> => {
        try {
            const response = await axios.put<T>(`${ApplicationApiEndpoint}${endpoint}`, body, {
                headers: { authorization: `Bearer ${await this.getToken()}` },
            });
            return response.data;
        } catch (ex) {
            if (this.is401(ex)) {
                try {
                    const response = await axios.put<T>(`${ApplicationApiEndpoint}${endpoint}`, body, {
                        headers: { authorization: `Bearer ${await this.refreshToken()}` },
                    });
                    return response.data;
                } catch {
                    return undefined;
                }
            }
            return undefined;
        }
    };

    protected patch = async <T>(endpoint: string, body: unknown = undefined): Promise<T | undefined> => {
        try {
            const response = await axios.patch<T>(`${ApplicationApiEndpoint}${endpoint}`, body, {
                headers: { authorization: `Bearer ${await this.getToken()}` },
            });
            return response.data;
        } catch (ex) {
            if (this.is401(ex)) {
                try {
                    const response = await axios.patch<T>(`${ApplicationApiEndpoint}${endpoint}`, body, {
                        headers: { authorization: `Bearer ${await this.refreshToken()}` },
                    });
                    return response.data;
                } catch {
                    return undefined;
                }
            }
            return undefined;
        }
    };

    protected postFormData = async <T>(endpoint: string, formData: FormData): Promise<T | undefined> => {
        try {
            const response = await axios.post<T>(`${ApplicationApiEndpoint}${endpoint}`, formData, {
                headers: { authorization: `Bearer ${await this.getToken()}` },
            });
            return response.data;
        } catch (ex) {
            if (this.is401(ex)) {
                try {
                    const response = await axios.post<T>(`${ApplicationApiEndpoint}${endpoint}`, formData, {
                        headers: { authorization: `Bearer ${await this.refreshToken()}` },
                    });
                    return response.data;
                } catch {
                    return undefined;
                }
            }
            return undefined;
        }
    };
}
